import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Search, User } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { canAccess } from "@/lib/permissions";

function localDayBounds() {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const date = `${y}-${m}-${day}`;
    return { dateFrom: date, dateTo: `${date}T23:59:59` };
}

function formatApptTime(iso) {
    try {
        return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
    } catch {
        return "";
    }
}

export default function TopBarSearch({ role }) {
    const navigate = useNavigate();
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const wrapRef = useRef(null);

    const showCustomers = canAccess(role, "customers");
    const showCalendar = canAccess(role, "calendar");

    useEffect(() => {
        const q = query.trim();
        if (q.length < 2) {
            setCustomers([]);
            setAppointments([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        const timer = setTimeout(async () => {
            try {
                const tasks = [];
                if (showCustomers) {
                    tasks.push(api.get("/customers", { params: { q } }).then((r) => r.data).catch(() => []));
                } else {
                    tasks.push(Promise.resolve([]));
                }
                if (showCalendar) {
                    const { dateFrom, dateTo } = localDayBounds();
                    const end = new Date();
                    end.setDate(end.getDate() + 14);
                    const endIso = end.toISOString().slice(0, 10) + "T23:59:59";
                    tasks.push(
                        api.get("/appointments", { params: { date_from: dateFrom, date_to: endIso } })
                            .then((r) => r.data)
                            .catch(() => []),
                    );
                } else {
                    tasks.push(Promise.resolve([]));
                }

                const [custRes, apptRes] = await Promise.all(tasks);
                const ql = q.toLowerCase();
                const custList = (custRes || []).slice(0, 5);
                const apptList = (apptRes || [])
                    .filter((a) => {
                        const name = a.customer?.full_name || "";
                        const mobile = a.customer?.mobile || "";
                        const svc = (a.items || []).map((it) => it.service_name || "").join(" ");
                        return (
                            name.toLowerCase().includes(ql)
                            || mobile.includes(q)
                            || svc.toLowerCase().includes(ql)
                        );
                    })
                    .slice(0, 5);

                setCustomers(custList);
                setAppointments(apptList);
            } finally {
                setLoading(false);
            }
        }, 280);

        return () => clearTimeout(timer);
    }, [query, showCustomers, showCalendar]);

    const hasResults = customers.length > 0 || appointments.length > 0;
    const showPanel = open && query.trim().length >= 2;

    const goCustomer = (c) => {
        setOpen(false);
        setQuery("");
        navigate(`/app/customers?q=${encodeURIComponent(c.full_name || c.mobile || "")}`);
    };

    const goCalendar = () => {
        setOpen(false);
        setQuery("");
        navigate("/app/calendar");
    };

    return (
        <Popover open={showPanel} onOpenChange={setOpen}>
            <PopoverAnchor asChild>
                <div ref={wrapRef} className="group relative w-full">
                    <Search
                        className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-[#8A8178] transition-colors group-focus-within:text-[#C9A227]"
                        strokeWidth={2}
                    />
                    <Input
                        type="search"
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setOpen(true);
                        }}
                        onFocus={() => query.trim().length >= 2 && setOpen(true)}
                        placeholder="Search customers, bookings…"
                        className="h-9 w-full rounded-full border-[#E8E4DE] bg-[#FAF9F6]/90 pl-9 pr-4 font-['Outfit',system-ui,sans-serif] text-xs text-[#2D2926] shadow-sm placeholder:text-[#9A9088] transition-all focus-visible:border-[#D4AF37]/55 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#D4AF37]/20"
                        data-testid="topbar-search"
                    />
                </div>
            </PopoverAnchor>
            <PopoverContent
                align="center"
                className="w-[min(320px,calc(100vw-2rem))] rounded-xl border-[#E8E4DE] p-0 font-['Outfit',system-ui,sans-serif] shadow-[0_12px_40px_rgba(45,41,38,0.1)]"
                onOpenAutoFocus={(e) => e.preventDefault()}
            >
                {loading && (
                    <p className="px-4 py-3 text-xs font-medium text-[#8A8178]">Searching…</p>
                )}
                {!loading && !hasResults && (
                    <p className="px-4 py-3 text-xs font-medium text-[#8A8178]">No matches found</p>
                )}
                {!loading && customers.length > 0 && (
                    <div className="border-b border-[#E8E4DE]/70">
                        <p className="px-4 py-2 text-[10px] font-bold uppercase tracking-wide text-[#8A8178]">Customers</p>
                        <ul>
                            {customers.map((c) => (
                                <li key={c.id}>
                                    <button
                                        type="button"
                                        className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm hover:bg-[#FAF9F6]"
                                        onClick={() => goCustomer(c)}
                                    >
                                        <User className="size-4 shrink-0 text-[#C9A227]" />
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate font-semibold text-[#2D2926]">{c.full_name}</span>
                                            <span className="block truncate text-xs text-[#8A8178]">{c.mobile}</span>
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {!loading && appointments.length > 0 && (
                    <div>
                        <p className="px-4 py-2 text-[10px] font-bold uppercase tracking-wide text-[#8A8178]">Bookings</p>
                        <ul>
                            {appointments.map((a) => (
                                <li key={a.id}>
                                    <button
                                        type="button"
                                        className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm hover:bg-[#FAF9F6]"
                                        onClick={goCalendar}
                                    >
                                        <Calendar className="size-4 shrink-0 text-[#4A0E17]" />
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate font-semibold text-[#2D2926]">
                                                {a.customer?.full_name || "Walk-in"}
                                            </span>
                                            <span className="block truncate text-xs capitalize text-[#8A8178]">
                                                {formatApptTime(a.start_at)} · {a.status}
                                            </span>
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
}
